import { useContext, useEffect, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { isStripeRedirectResponse } from '@/types/guards';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import type { StripePaymentResponse } from '@/types/payment';

/**
 * Generic Stripe `handleNextAction` driver. Despite the name, this is the
 * single entry point for *any* Stripe `requires_action` flow:
 *   - Cards: `next_action.type === 'redirect_to_url'` → 3DS popup.
 *   - GPay / ApplePay: `next_action.type === 'use_stripe_sdk'` → inline
 *     3DS modal mounted by Stripe.js into the document body.
 *
 * The publishable key matters: for publishers that issue different sub-MIDs
 * per payment method, the wallet PaymentIntent was created against the
 * Express Stripe account. Calling `handleNextAction` on the regular Stripe
 * instance in that setup returns "PaymentIntent not found". We therefore
 * pick `stripeService.getStripeExpress()` whenever the selected method is a
 * wallet, and fall back to the regular `stripe` from context otherwise.
 */
export const StripeCard3DSForm = () => {
  const {
    threeDSSecurityState: { params, paymentMethod, handlePaymentSuccess, handlePaymentError },
    stripe,
    paymentMethodSelected,
  } = useContext(CheckoutContext);
  const isProcessing = useRef(false);

  useEffect(() => {
    if (!params || !isStripeRedirectResponse(params, ProviderEnum.STRIPE)) {
      return;
    }

    if (isProcessing.current) {
      return;
    }

    // `paymentMethod` from threeDSSecurityState is authoritative because
    // express GPay/ApplePay don't update context's `paymentMethodSelected`.
    // Fall back to context for the regular (non-express) card flow.
    const activeMethod = paymentMethod ?? paymentMethodSelected;
    const isWallet =
      activeMethod === PaymentMethodEnum.GOOGLE_PAY || activeMethod === PaymentMethodEnum.APPLE_PAY;

    let stripeForNextAction = stripe;
    if (isWallet) {
      try {
        stripeForNextAction = stripeService.getStripeExpress();
      } catch {
        // Express instance not initialized — falls back to the regular
        // `stripe`. Fine for single-account publishers (same key on both
        // instances). Only multi-account publishers hit the mismatch case.
      }
    }
    if (!stripeForNextAction) {
      return;
    }

    isProcessing.current = true;

    stripeForNextAction
      .handleNextAction({
        clientSecret: (params as StripePaymentResponse).stripe.clientSecret,
      })
      .then((result) => {
        if (result.error) {
          handlePaymentError?.();
        } else {
          handlePaymentSuccess?.();
        }
      })
      .catch((error) => {
        console.error('Stripe handleNextAction error:', error);
        handlePaymentError?.();
      })
      .finally(() => {
        isProcessing.current = false;
      });
  }, [params, handlePaymentSuccess, handlePaymentError, stripe, paymentMethod, paymentMethodSelected]);
  return null;
};
